/**
 * Recently Viewed Products
 * Tracks product views and renders recently viewed products section
 */

(function() {
  'use strict';

  const STORAGE_KEY = 'recentlyViewedProducts';
  const MAX_PRODUCTS = 5;

  const RecentlyViewedProducts = {
    /**
     * Initialize the module
     */
    init: function() {
      // Track current product view if on product page
      this.trackProductView();
      
      // Render recently viewed section if it exists on the page
      this.renderRecentlyViewed();
    },

    /**
     * Track the current product being viewed
     */
    trackProductView: function() {
      // Check if we're on a product page
      const productHandle = this.getCurrentProductHandle();
      
      if (!productHandle) {
        return;
      }

      // Get existing viewed products
      let viewedProducts = this.getViewedProducts();

      // Remove the current product if it already exists (to move it to the front)
      viewedProducts = viewedProducts.filter(handle => handle !== productHandle);

      // Add current product to the beginning
      viewedProducts.unshift(productHandle);

      // Keep only the last MAX_PRODUCTS
      if (viewedProducts.length > MAX_PRODUCTS) {
        viewedProducts = viewedProducts.slice(0, MAX_PRODUCTS);
      }

      // Save to localStorage
      this.saveViewedProducts(viewedProducts);
    },

    /**
     * Get the current product handle from the page
     */
    getCurrentProductHandle: function() {
      // Try to get from meta tags
      const metaProduct = document.querySelector('meta[property="og:url"]');
      if (metaProduct) {
        const urlParts = metaProduct.content.split('/products/');
        if (urlParts.length > 1) {
          return urlParts[1].split('?')[0].split('#')[0];
        }
      }

      // Try to get from URL
      if (window.location.pathname.includes('/products/')) {
        const pathParts = window.location.pathname.split('/products/');
        if (pathParts.length > 1) {
          return pathParts[1].split('?')[0].split('#')[0];
        }
      }

      return null;
    },

    /**
     * Get viewed products from localStorage
     */
    getViewedProducts: function() {
      try {
        const stored = localStorage.getItem(STORAGE_KEY);
        return stored ? JSON.parse(stored) : [];
      } catch (e) {
        console.error('Error reading from localStorage:', e);
        return [];
      }
    },

    /**
     * Save viewed products to localStorage
     */
    saveViewedProducts: function(products) {
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(products));
      } catch (e) {
        console.error('Error writing to localStorage:', e);
      }
    },

    /**
     * Render recently viewed products section
     */
    renderRecentlyViewed: function() {
      const sections = document.querySelectorAll('[data-section-type="recently-viewed-products"]');
      
      if (sections.length === 0) {
        return;
      }

      sections.forEach(section => {
        this.renderSection(section);
      });
    },

    /**
     * Render a single recently viewed section
     */
    renderSection: function(section) {
      const sectionId = section.dataset.sectionId;
      const currentProduct = section.dataset.currentProduct;
      const limit = parseInt(section.dataset.limit) || MAX_PRODUCTS;
      
      // Get viewed products
      let viewedProducts = this.getViewedProducts();

      // Exclude current product
      if (currentProduct) {
        viewedProducts = viewedProducts.filter(handle => handle !== currentProduct);
      }

      // Limit to the specified number
      viewedProducts = viewedProducts.slice(0, limit);

      // If no products to show, hide the section
      if (viewedProducts.length === 0) {
        section.style.display = 'none';
        return;
      }

      // Fetch and render products
      this.fetchAndRenderProducts(section, sectionId, viewedProducts);
    },

    /**
     * Fetch product data and render them
     */
    fetchAndRenderProducts: function(section, sectionId, productHandles) {
      const gridContainer = section.querySelector('#RecentlyViewedGrid-' + sectionId);
      
      if (!gridContainer) {
        return;
      }

      // Fetch all products
      const fetchPromises = productHandles.map(handle => 
        fetch('/products/' + handle + '.js')
          .then(response => {
            if (!response.ok) {
              throw new Error('Product not found');
            }
            return response.json();
          })
          .catch(error => {
            console.warn('Could not fetch product:', handle, error);
            return null;
          })
      );

      Promise.all(fetchPromises).then(products => {
        // Filter out null products (failed fetches)
        const validProducts = products.filter(p => p !== null);

        if (validProducts.length === 0) {
          section.style.display = 'none';
          return;
        }

        // Render products
        gridContainer.innerHTML = '';
        validProducts.forEach((product, index) => {
          const productHTML = this.createProductHTML(product, index + 1, section.dataset);
          gridContainer.insertAdjacentHTML('beforeend', productHTML);
        });

        // Show the section
        section.style.display = '';

        // Initialize carousel if enabled
        if (section.dataset.slider === 'true') {
          this.initializeCarousel(gridContainer, section.dataset);
        }
      });
    },

    /**
     * Create HTML for a product grid item
     */
    createProductHTML: function(product, current, sectionData) {
      const variant = product.variants && product.variants.length ? product.variants[0] : null;
      const firstAvailable = (product.variants || []).find(v => v.available) || variant;
      const image = product.featured_image || (product.images && product.images[0]);
      const imageUrl = image
        ? (typeof image === 'string'
            ? this.getImageUrl(image, '400x')
            : (image.src ? this.getImageUrl(image.src, '400x') : ''))
        : '';
      const compareAtPrice = variant && variant.compare_at_price;
      const price = variant && variant.price;
      const onSale = !!(compareAtPrice && price && compareAtPrice > price);
      const soldOut = !(product.available);
      const productUrl = '/products/' + product.handle;

      // Grid item width classes (align with theme)
      const desktopGrid = parseInt(sectionData.desktopGrid) || 4;
      const mobileGrid = parseInt(sectionData.mobileGrid) || 2;
      const gridItemWidth = this.getGridItemWidth(desktopGrid, mobileGrid);

      // Title and vendor
      const title = product.title || '';
      const vendor = product.vendor || '';

      // Build markup mirroring snippets/product-grid-item.liquid structure and classes
      return `
        <div class="grid__item grid-product flex ${gridItemWidth}${soldOut ? ' is-sold-out' : ''}" data-product-card data-product-handle="${product.handle}" data-product-id="${product.id}">
          <div class="card grid-product__wrapper overflow-hidden text-">
            <div class="grid-product__image-wrapper">
              <a href="${productUrl}${firstAvailable ? `?variant=${firstAvailable.id}` : ''}" class="grid-product__image-link">
                ${imageUrl ? `<img class=\"grid-product__image\" src=\"${imageUrl}\" alt=\"${this.escapeHtml((image && typeof image === 'object' && image.alt) ? image.alt : title)}\">` : ''}
              </a>
            </div>

            <a href="${productUrl}${firstAvailable ? `?variant=${firstAvailable.id}` : ''}" class="card-body grid-product__meta spacer-auto-xs text-center">
              <p class="grid-product__title h6">${this.escapeHtml(title)}</p>
              <p class="grid-product__price-wrap">
                <span class="grid-product__price text-money">
                  <span class="price-regular${onSale ? ' on-sale text-sale' : ''}" aria-label="${onSale ? 'Sale price' : 'Regular price'}">
                    <span class="money">${this.formatMoney(price)}</span>
                  </span>
                  &nbsp;
                  <span class="price-compare text-strike text-muted ${onSale ? '' : 'hide'}" aria-label="Regular price">
                    <span class="money">${onSale ? this.formatMoney(compareAtPrice) : ''}</span>
                  </span>
                </span>
              </p>
            </a>

            ${firstAvailable ? `
            <form method="post" action="/cart/add" style="display: flex; justify-content: space-around;">
              <input type="hidden" name="id" value="${firstAvailable.id}" />
              <input type="submit" class="btn" style="padding: 6px 10px;" value="Adauga in cos" />
            </form>` : ''}

          </div>
        </div>
      `;
    },

    /**
     * Get grid item width classes
     */
    getGridItemWidth: function(desktopGrid, mobileGrid) {
      const desktopClass = {
        2: 'medium-up--one-half',
        3: 'medium-up--one-third',
        4: 'medium-up--one-quarter'
      }[desktopGrid] || 'medium-up--one-quarter';

      const mobileClass = {
        1: 'small--one-whole',
        2: 'small--one-half'
      }[mobileGrid] || 'small--one-half';

      return `${mobileClass} ${desktopClass}`;
    },

    /**
     * Get image URL with size parameter
     */
    getImageUrl: function(imageUrl, size) {
      if (!imageUrl) return '';
      
      // Shopify image URL format: filename.extension => filename_size.extension
      const match = imageUrl.match(/^(.*)\.([^.]+)$/);
      if (match) {
        return match[1] + '_' + size + '.' + match[2];
      }
      return imageUrl;
    },

    /**
     * Format money (basic implementation)
     */
    formatMoney: function(cents) {
      if (typeof cents === 'undefined' || cents === null) {
        return '';
      }
      
      const amount = (cents / 100).toFixed(2);
      
      // Try to use theme's money format if available
      if (typeof Shopify !== 'undefined' && Shopify.formatMoney) {
        return Shopify.formatMoney(cents);
      }
      
      return amount + ' lei';
    },

    /**
     * Escape HTML to prevent XSS
     */
    escapeHtml: function(text) {
      const div = document.createElement('div');
      div.textContent = text;
      return div.innerHTML;
    },

    /**
     * Initialize Slick carousel
     */
    initializeCarousel: function(gridContainer, sectionData) {
      // Check if jQuery and Slick are available
      if (typeof jQuery === 'undefined' || typeof jQuery.fn.slick === 'undefined') {
        console.warn('Slick carousel not available');
        return;
      }

      const $grid = jQuery(gridContainer);
      
      // Destroy existing slick if any
      if ($grid.hasClass('slick-initialized')) {
        $grid.slick('unslick');
      }

      // Initialize slick
      $grid.slick({
        slidesToShow: parseInt(sectionData.desktopGrid) || 4,
        slidesToScroll: 1,
        arrows: sectionData.arrows === 'true',
        dots: sectionData.dots === 'true',
        autoplay: sectionData.autoplay === 'true',
        autoplaySpeed: parseInt(sectionData.autoplayspeed) || 5000,
        responsive: [
          {
            breakpoint: 768,
            settings: {
              slidesToShow: parseInt(sectionData.mobileGrid) || 2,
              slidesToScroll: 1
            }
          }
        ]
      });
    }
  };

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function() {
      RecentlyViewedProducts.init();
    });
  } else {
    RecentlyViewedProducts.init();
  }

  // Expose to window for manual initialization if needed
  window.RecentlyViewedProducts = RecentlyViewedProducts;

})();
